import {
  useQueryParam,
  StringParam,
  NumberParam,
  useQueryParams,
} from 'use-query-params';
import { useMemo } from 'react';
import { useDebounce } from './useDebounce';
import type { IBaseTableParams } from '../types';
import { normalizeDataIndex } from '../ui-builder';

export const isPaginate = (extra: any) => {
  if (extra.action === 'paginate') {
    return true;
  }
};

export const isSort = (extra: any) => {
  if (extra.action === 'sort') {
    return true;
  }
};

export const isFilter = (extra: any) => {
  if (extra.action === 'filter') {
    return true;
  }
};

const buildQueryName = (query: string, prefix?: string) => {
  if (prefix) return `${prefix}_${query}`;

  return query;
};

const useTableQueryParams = ({
  prefix,
  initialParams,
}: { prefix?: string; initialParams?: Partial<IBaseTableParams> } = {}) => {
  // Compute concrete query keys (with optional prefix) once
  const pageKey = buildQueryName('page', prefix);
  const sizeKey = buildQueryName('size', prefix);
  const sortNameKey = buildQueryName('sort_name', prefix);
  const sortOrderKey = buildQueryName('sort_order', prefix);

  const [queryPage, setQueryPage] = useQueryParam(pageKey, NumberParam);
  const page = queryPage || 1;
  const [querySize, _] = useQueryParam(sizeKey, NumberParam);
  const size = querySize || initialParams?.size || 10;
  const [sortName, setSortName] = useQueryParam(sortNameKey, StringParam);
  const [sortOrder, setSortOrder] = useQueryParam(sortOrderKey, StringParam);

  // Atomic setter for multiple params in one navigation
  const [, setQueryParams] = useQueryParams({
    [pageKey]: NumberParam,
    [sizeKey]: NumberParam,
    [sortNameKey]: StringParam,
    [sortOrderKey]: StringParam,
  } as Record<string, any>);

  const queryParams: IBaseTableParams = useMemo(
    () => ({
      page,
      size,
      sortName,
      sortOrder,
    }),
    [page, size, sortName, sortOrder]
  );

  const debouncedValue: IBaseTableParams = useDebounce(queryParams, 500);

  const handleChange = (pagination: any, _: any, sorter: any, extra: any) => {
    const next: Record<string, any> = {};
    if (isPaginate(extra)) {
      next[pageKey] = pagination.current;
      if (pagination.pageSize !== undefined) {
        next[sizeKey] = pagination.pageSize;
      }
    }
    if (isSort(extra)) {
      next[sortNameKey] =
        sorter.order &&
        (sorter.column?.sortName ||
          normalizeDataIndex(sorter.column.dataIndex));
      next[sortOrderKey] = sorter.order;
      next[pageKey] = 1;
    }
    if (Object.keys(next).length) setQueryParams(next as any);
  };

  const handleSetSize = (perPage: number) => {
    setQueryParams({ [sizeKey]: perPage, [pageKey]: 1 } as any);
  };

  const handleResetPage = () => {
    setQueryParams({ [pageKey]: 1 } as any);
  };

  return {
    page,
    setQueryPage,
    size,
    setSize: handleSetSize,
    sortName,
    setSortName,
    sortOrder: sortOrder as 'ascend' | 'descend',
    setSortOrder,
    handleChange,
    value: queryParams,
    debouncedValue,
    queryPage,
    handleResetPage,
  };
};

export default useTableQueryParams;
